import { useCallback, useMemo, useState } from 'react';
import { xml2js } from 'xml-js';

import { Icons, Modal, PageTitle, Table } from '@components/common';
import FlatTreeXML from '@components/common/FlatXMLTree';

import { useMutateLockRUDashboardStatus } from '@hooks/ruDashboardState/useMutateLockRUDashboardStatus';
import { useQueryRUDashboardInterface } from '@hooks/ruDashboardState/useQueryRUDashboardInterface';
import { useQueryRUDashboardStatus } from '@hooks/ruDashboardState/useQueryRUDashboardStatus';
import { useQueryRUDashboardStatusAdministrativeState } from '@hooks/ruDashboardState/useQueryRUDashboardStatusAdministrativeState';
import { useQueryRUDashboardStatusAvailabilityState } from '@hooks/ruDashboardState/useQueryRUDashboardStatusAvailabilityState';
import { useQueryRUDashboardStatusOperationalState } from '@hooks/ruDashboardState/useQueryRUDashboardStatusOperationalState';
import { useQueryRUDashboardStatusPowerState } from '@hooks/ruDashboardState/useQueryRUDashboardStatusPowerState';
import { useQueryRUDashboardStatusSynchronizationState } from '@hooks/ruDashboardState/useQueryRUDashboardStatusSynchronizationState';
import { useQueryRUDashboardStatusUsageState } from '@hooks/ruDashboardState/useQueryRUDashboardStatusUsageState';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { convertXMLToFlatTreeData } from '@components/common/FlatXMLTree/utils';

import { TSelectOption } from '@interfaces/components/componentTypes';
import {
	ISortBy,
	ITablePagination,
	ITableSearch,
	ITableSort,
	TTableFilter,
} from '@interfaces/components/table';
import { IRUStatusDashboard } from '@interfaces/ruStatusDashboard/ruStatusDashboard';
import { INonCompactXMLData, TMapTree } from '@components/common/FlatXMLTree/interface';

import { defaultMeta, EAdminPanelPageTitles, ELockedUnlocked } from '@enums/common';

import { colors } from '@constants/colors';
import { MIN_FILTER_WIDTH } from '@constants/common';
import { MODAL_SIZE_80_PERCENT } from '@constants/modals';
import { TableFilterTypes } from '@constants/table';

import CarrierStateTable from './CarrierStateTable';
import { getColumns } from './utils';

import styles from './RUStatusDashboard.module.scss';

const RUStatusDashboard = () => {
	const {
		accessPermission: {
			smoLightUpdater,
			smoLightViewer,
		},
	} = useAppSelector(auth);

	const [
		selectedRUStatus,
		setSelectedRUStatus,
	] = useState<IRUStatusDashboard | undefined>();

	const [isOpenCarrierStateTable, setIsOpenCarrierStateTable] = useState(false);
	const [isTreeModalOpen, setIsTreeModalOpen] = useState(false);
	const [treeData, setTreeData] = useState<TMapTree>(new Map());

	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [searchValue, setSearchValue] = useState<string>('');
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);

	const [selectedAdministrativeState, setSelectedAdministrativeState] = useState<TSelectOption<string>>(null);
	const [selectedOperationalState, setSelectedOperationalState] = useState<TSelectOption<string>>(null);
	const [selectedUsageState, setSelectedUsageState] = useState<TSelectOption<string>>(null);
	const [selectedPowerState, setSelectedPowerState] = useState<TSelectOption<string>>(null);
	const [selectedAvailabilityState, setSelectedAvailabilityState] = useState<TSelectOption<string>>(null);
	const [selectedSynchronizationState, setSelectedSynchronizationState] = useState<TSelectOption<string>>(null);

	const {
		ruDashboardStatus,
		isLoadingRUDashboardStatus,
		countOfRecords,
	} = useQueryRUDashboardStatus({
		page,
		pageSize,
		searchValue,
		sortFieldName,
		sortDirection,
		administrativeState: selectedAdministrativeState?.value || undefined,
		operationalState: selectedOperationalState?.value || undefined,
		usageState: selectedUsageState?.value || undefined,
		powerState: selectedPowerState?.value || undefined,
		availabilityState: selectedAvailabilityState?.value || undefined,
		synchronisationState: selectedSynchronizationState?.value || undefined,
	});

	const {
		ruDashboardStatusAdministrativeState,
		isLoadingRUDashboardStatusAdministrative,
	} = useQueryRUDashboardStatusAdministrativeState();

	const {
		ruDashboardStatusOperationalState,
		isLoadingRUDashboardStatusOperational,
	} = useQueryRUDashboardStatusOperationalState();

	const {
		ruDashboardStatusUsageState,
		isLoadingRUDashboardStatusUsage,
	} = useQueryRUDashboardStatusUsageState();

	const {
		ruDashboardStatusPowerState,
		isLoadingRUDashboardStatusPower,
	} = useQueryRUDashboardStatusPowerState();

	const {
		ruDashboardStatusAvailabilityState,
		isLoadingRUDashboardStatusAvailability,
	} = useQueryRUDashboardStatusAvailabilityState();

	const {
		ruDashboardInterface,
	} = useQueryRUDashboardInterface({
		ruNetworkElementId: selectedRUStatus?.id || '',
	});

	const {
		ruDashboardStatusSynchronizationState,
		isLoadingRUDashboardStatusSynchronization,
	} = useQueryRUDashboardStatusSynchronizationState();

	const {
		lockRUStatusDashboardAdministrativeState,
		isLoadingLockRUDashboardStatus,
	} = useMutateLockRUDashboardStatus();

	const isViewerOnly = !smoLightUpdater && smoLightViewer;

	const rawTreeData = ruDashboardInterface || '';

	useMemo(() => {
		if (!rawTreeData) {
			setTreeData(new Map());
			return;
		}

		try {
			const result = xml2js(rawTreeData, { compact: false }) as { elements: INonCompactXMLData[] };
			setTreeData(convertXMLToFlatTreeData(result.elements || []));
		} catch (error) {
			setTreeData(new Map());
		}
	}, [rawTreeData]);

	const toggleTreeModal = useCallback(() => {
		setIsTreeModalOpen((prevState) => !prevState);
		setSelectedRUStatus(undefined);
	}, []);

	const openCarrierStateTableModal = useCallback(() => setIsOpenCarrierStateTable(true), []);
	const closeCarrierStateModal = useCallback(() => {
		setIsOpenCarrierStateTable(false);
	}, []);

	const toggleAvailability = useCallback((id: string, isEnabled: boolean) => {
		lockRUStatusDashboardAdministrativeState({
			id,
			adminState: isEnabled ? ELockedUnlocked.locked : ELockedUnlocked.unlocked,
			isEnergySavingMode: selectedRUStatus?.isEnergySavingMode,
		});
	}, [lockRUStatusDashboardAdministrativeState, selectedRUStatus]);

	const toggleEnergySavingMode = useCallback((id: string, isEnabled: boolean) => {
		lockRUStatusDashboardAdministrativeState({
			id,
			adminState: selectedRUStatus?.administrativeState?.name,
			isEnergySavingMode: isEnabled,
		});
	}, [lockRUStatusDashboardAdministrativeState, selectedRUStatus]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const columns = useMemo(() => getColumns({
		isViewerOnly,
		openCarrierStateTableModal,
		toggleTreeModal,
		setSelectedRUStatus,
		toggleAvailability,
		toggleEnergySavingMode,
	}), [
		isViewerOnly,
		openCarrierStateTableModal,
		toggleTreeModal,
		toggleAvailability,
		toggleEnergySavingMode,
	]);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const handleAdministrativeStateChange = useCallback((option: TSelectOption<string>) => {
		setSelectedAdministrativeState(option);
		setPage(defaultMeta.page);
	}, []);

	const handleOperationalStateChange = useCallback((option: TSelectOption<string>) => {
		setSelectedOperationalState(option);
		setPage(defaultMeta.page);
	}, []);

	const handleUsageStateChange = useCallback((option: TSelectOption<string>) => {
		setSelectedUsageState(option);
		setPage(defaultMeta.page);
	}, []);

	const handlePowerStateChange = useCallback((option: TSelectOption<string>) => {
		setSelectedPowerState(option);
		setPage(defaultMeta.page);
	}, []);

	const handleAvailabilityStateChange = useCallback((option: TSelectOption<string>) => {
		setSelectedAvailabilityState(option);
		setPage(defaultMeta.page);
	}, []);

	const handleSynchronizationStateChange = useCallback((option: TSelectOption<string>) => {
		setSelectedSynchronizationState(option);
		setPage(defaultMeta.page);
	}, []);

	const search: Array<ITableSearch> = useMemo(() => [{
		initialValue: searchValue,
		name: 'searchValue',
		handler: searchHandler,
		tooltipText: 'Search by name',
	}], [searchValue, searchHandler]);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'administrativeState',
				placeholder: 'Administrative state',
				option: selectedAdministrativeState,
				options: ruDashboardStatusAdministrativeState,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAdministrativeStateChange,
				defaultValue: null,
			},
			{
				name: 'operationalState',
				placeholder: 'Operational state',
				option: selectedOperationalState,
				options: ruDashboardStatusOperationalState,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleOperationalStateChange,
				defaultValue: null,
			},
			{
				name: 'usageState',
				placeholder: 'Usage state',
				option: selectedUsageState,
				options: ruDashboardStatusUsageState,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleUsageStateChange,
				defaultValue: null,
			},
			{
				name: 'powerState',
				placeholder: 'Power state',
				option: selectedPowerState,
				options: ruDashboardStatusPowerState,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handlePowerStateChange,
				defaultValue: null,
			},
			{
				name: 'availabilityState',
				placeholder: 'Availability state',
				option: selectedAvailabilityState,
				options: ruDashboardStatusAvailabilityState,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleAvailabilityStateChange,
				defaultValue: null,
			},
			{
				name: 'synchronizationState',
				placeholder: 'Synchronization state',
				option: selectedSynchronizationState,
				options: ruDashboardStatusSynchronizationState,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handleSynchronizationStateChange,
				defaultValue: null,
			},
		];
	}, [
		selectedAdministrativeState,
		selectedOperationalState,
		selectedUsageState,
		selectedPowerState,
		selectedAvailabilityState,
		selectedSynchronizationState,
		ruDashboardStatusAdministrativeState,
		ruDashboardStatusOperationalState,
		ruDashboardStatusUsageState,
		ruDashboardStatusPowerState,
		ruDashboardStatusAvailabilityState,
		ruDashboardStatusSynchronizationState,
		handleAdministrativeStateChange,
		handleOperationalStateChange,
		handleUsageStateChange,
		handlePowerStateChange,
		handleAvailabilityStateChange,
		handleSynchronizationStateChange,
	]);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const isLoading = isLoadingRUDashboardStatus
		|| isLoadingRUDashboardStatusAdministrative
		|| isLoadingRUDashboardStatusOperational
		|| isLoadingRUDashboardStatusUsage
		|| isLoadingRUDashboardStatusPower
		|| isLoadingRUDashboardStatusAvailability
		|| isLoadingRUDashboardStatusSynchronization
		|| isLoadingLockRUDashboardStatus;

	return (
		<div className={styles.wrapper}>
			<Modal
				height={MODAL_SIZE_80_PERCENT}
				isConfirmDialogNeededOnClose
				isOpen={isOpenCarrierStateTable}
				onClose={closeCarrierStateModal}
				title='TX/RX Carrier State'
				width={MODAL_SIZE_80_PERCENT}
			>
				<CarrierStateTable
					carrierName={selectedRUStatus?.ruName}
					id={selectedRUStatus?.id || ''}
					isEnergySavingMode={selectedRUStatus?.isEnergySavingMode || false}
					name={selectedRUStatus?.modelName || ''}
					title={selectedRUStatus?.modelName || ''}
				/>
			</Modal>
			{isTreeModalOpen && treeData.size > 0 && (
				<Modal
					height={MODAL_SIZE_80_PERCENT}
					isOpen
					onClose={toggleTreeModal}
					title={`O-RU interfaces state and statistics - ${selectedRUStatus?.ruName}`}
					width='840px'
				>
					<FlatTreeXML
						data={treeData}
						isReadOnly
						isWithLineNumbers={false}
						isWithToggle={false}
						setData={setTreeData}
						withSearch={false}
					/>
				</Modal>
			)}
			<PageTitle
				icon={Icons.middlewareSMO({ isActive: true, color: colors.white })}
				title={EAdminPanelPageTitles.RUStatusDashboard}
			/>
			<div className={styles.tableContainer}>
				<Table
					columns={columns}
					data={ruDashboardStatus}
					filters={filters}
					isLoading={isLoading}
					isPinColumns
					isWithoutActions
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1]}
				/>
			</div>
		</div>
	);
};

export default RUStatusDashboard;
